import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, Lock, Crown, ShieldCheck, ImagePlus, X as XIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import OnlineDot from '../components/OnlineDot';
import ProfileDetailModal from '../components/ProfileDetailModal';
import VoiceRecorder from '../components/VoiceRecorder';
import VoiceMessagePlayer from '../components/VoiceMessagePlayer';
import { useAuth } from '../contexts/AuthContext';
import { getMessages, sendMessage, sendMessagePhoto, sendMessageVoice, getMatches, getMessagingSettings } from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbsUrl = (u) => (!u ? u : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));

const ChatPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null); // { file, previewUrl, caption }
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [sendingVoice, setSendingVoice] = useState(false);
  const [lightbox, setLightbox] = useState(null); // ISO url of enlarged photo
  const [paywall, setPaywall] = useState(null); // { title, subtitle, pendingText? }
  const messagesEndRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadData = async () => {
    try {
      const [matchesRes, settingsRes] = await Promise.all([
        getMatches(),
        getMessagingSettings()
      ]);
      
      const foundMatch = matchesRes.data.find(m => m.match_id === matchId);
      if (foundMatch) {
        setMatch(foundMatch);
      }
      setSettings(settingsRes.data);
      
      await loadMessages();

      // Restore pending draft if user returned from Buy Credits / Subscribe
      try {
        const raw = localStorage.getItem('gv_pending_msg');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.match_id === matchId && parsed?.text && (Date.now() - (parsed.ts || 0)) < 30 * 60 * 1000) {
            setNewMessage(parsed.text);
            localStorage.removeItem('gv_pending_msg');
          }
        }
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await getMessages(matchId);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const openPaywall = (kind, pendingText = '') => {
    if (pendingText) {
      try {
        localStorage.setItem('gv_pending_msg', JSON.stringify({
          match_id: matchId, text: pendingText, kind, ts: Date.now()
        }));
      } catch { /* ignore */ }
    }
    setPaywall({
      title: kind === 'voice' ? 'Voice notes need credits' : kind === 'photo' ? 'Photos need credits' : 'Messages need credits',
      subtitle: "Buy credits or subscribe to keep the conversation going. We'll bring you right back here."
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setError('');

    try {
      await sendMessage(match.user.user_id, newMessage.trim());
      setNewMessage('');
      // Optimistically show and refresh
      await loadMessages();
    } catch (err) {
      if (err.response?.status === 402) {
        openPaywall('text', newMessage);
      } else {
        setError(err.response?.data?.detail || 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const handlePickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Photo must be smaller than 10MB.');
      return;
    }
    setError('');
    setPendingPhoto({ file, previewUrl: URL.createObjectURL(file), caption: '' });
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const cancelPendingPhoto = () => {
    if (pendingPhoto?.previewUrl) URL.revokeObjectURL(pendingPhoto.previewUrl);
    setPendingPhoto(null);
  };

  const handleSendPhoto = async () => {
    if (!pendingPhoto || uploadingPhoto) return;
    setUploadingPhoto(true);
    setError('');
    try {
      await sendMessagePhoto(matchId, pendingPhoto.file, pendingPhoto.caption || '');
      cancelPendingPhoto();
      await loadMessages();
    } catch (err) {
      if (err.response?.status === 402) {
        openPaywall('photo');
      } else {
        setError(err.response?.data?.detail || 'Failed to send photo');
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSendVoice = async (file, durationSeconds) => {
    if (sendingVoice) return;
    setSendingVoice(true);
    setError('');
    try {
      await sendMessageVoice(matchId, file, durationSeconds);
      await loadMessages();
    } catch (err) {
      if (err.response?.status === 402) {
        openPaywall('voice');
      } else {
        setError(err.response?.data?.detail || 'Failed to send voice note');
      }
      throw err;
    } finally {
      setSendingVoice(false);
    }
  };

  const defaultPhoto = useDefaultAvatar() || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=200';

  const otherUser = match?.user;
  const primaryPhoto = otherUser?.photos?.[otherUser?.primary_photo_index ?? 0] || otherUser?.photos?.[0] || defaultPhoto;

  const totalBalance = (user?.credits || 0) + (user?.promo_credits || 0);
  const isPaid = settings?.mode === 'paid';
  const canMessage = user?.is_premium || (totalBalance > 0) || !isPaid;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#009E60] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/messages')}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button
            onClick={() => otherUser?.user_id && setShowProfile(true)}
            className="flex items-center gap-3 flex-1 min-w-0 -mx-1 px-1 py-1 rounded-lg hover:bg-gray-50 transition-colors text-left"
            data-testid="chat-header-profile"
          >
            <div className="relative flex-shrink-0">
              <img
                src={primaryPhoto}
                alt={otherUser?.name || 'Member'}
                className="w-11 h-11 rounded-full object-cover border border-gray-100"
              />
              {otherUser?.is_online && (
                <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h2 className="font-semibold text-gray-900 truncate">
                  {otherUser?.display_name || otherUser?.name || 'Member'}
                  {otherUser?.age ? `, ${otherUser.age}` : ''}
                </h2>
                {otherUser?.is_verified && (
                  <ShieldCheck className="w-4 h-4 text-blue-500 flex-shrink-0" title="Verified" />
                )}
                {match?.direct && (
                  <span className="text-[10px] font-medium bg-[#FFCE00]/20 text-[#8a6d00] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <Crown className="w-2.5 h-2.5" /> Direct
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {otherUser?.is_online
                  ? <span className="text-emerald-600 font-medium">Online now</span>
                  : <OnlineDot isOnline={false} lastActive={otherUser?.last_active} size={6} showLabel={true} />}
                {(otherUser?.current_city || otherUser?.location) && (
                  <span className="text-gray-400"> · {otherUser.current_city || otherUser.location}</span>
                )}
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto pt-20 pb-24 px-4">
        <div className="max-w-lg mx-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-[#009E60]/10 flex items-center justify-center mx-auto mb-4">
                <img
                  src={primaryPhoto}
                  alt={otherUser?.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{otherUser?.display_name || otherUser?.name}</h3>
              <p className="text-gray-500 text-sm">
                {match?.direct ? 'Start your conversation.' : 'You matched! Start the conversation.'}
              </p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isMe = msg.sender_id === user?.user_id;
              const isPhoto = msg.message_type === 'photo' && msg.attachment_url;
              const isVoice = msg.message_type === 'voice' && msg.attachment_url;
              return (
                <motion.div
                  key={msg.message_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] ${
                      isPhoto
                        ? 'rounded-2xl overflow-hidden shadow-sm'
                        : isVoice
                          ? (isMe ? 'message-sent px-3 py-2' : 'message-received px-3 py-2')
                          : (isMe ? 'message-sent px-4 py-3' : 'message-received px-4 py-3')
                    }`}
                  >
                    {isPhoto ? (
                      <>
                        <button
                          onClick={() => setLightbox(toAbsUrl(msg.attachment_url))}
                          className="block"
                          data-testid={`photo-message-${msg.message_id}`}
                        >
                          <img
                            src={toAbsUrl(msg.attachment_url)}
                            alt={msg.content || 'Photo message'}
                            className="max-w-[280px] max-h-[360px] w-full h-auto object-cover"
                            loading="lazy"
                          />
                        </button>
                        {msg.content && (
                          <div className={`px-3 py-2 text-sm ${isMe ? 'bg-[#009E60] text-white' : 'bg-gray-100 text-gray-800'}`}>
                            {msg.content}
                          </div>
                        )}
                        <div className={`px-3 py-1 text-[10px] ${isMe ? 'bg-[#009E60] text-white/70' : 'bg-gray-100 text-gray-400'}`}>
                          {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                        </div>
                      </>
                    ) : isVoice ? (
                      <>
                        <VoiceMessagePlayer
                          src={toAbsUrl(msg.attachment_url)}
                          durationSeconds={msg.duration_seconds || 0}
                          isMe={isMe}
                          messageId={msg.message_id}
                        />
                        <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-gray-400'}`}>
                          {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                        </p>
                      </>
                    ) : (
                      <>
                        <p>{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-gray-400'}`}>
                          {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                        </p>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 safe-bottom">
        <div className="max-w-lg mx-auto px-4 py-3">
          {error && (
            <div className="mb-2 p-2 bg-red-50 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
          
          {!canMessage ? (
            <div className="flex items-center justify-between bg-[#FFCE00]/10 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Lock className="w-5 h-5" />
                <span className="text-sm">Subscribe to send messages</span>
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
                size="sm"
                data-testid="subscribe-btn"
              >
                <Crown className="w-4 h-4 mr-1" />
                Upgrade
              </Button>
            </div>
          ) : pendingPhoto ? (
            <div className="border border-gray-100 rounded-2xl p-2" data-testid="photo-preview">
              <div className="flex items-start gap-3">
                <div className="relative flex-shrink-0">
                  <img src={pendingPhoto.previewUrl} alt="Preview" className="w-20 h-20 rounded-xl object-cover" />
                  <button
                    onClick={cancelPendingPhoto}
                    disabled={uploadingPhoto}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full shadow border border-gray-100 flex items-center justify-center hover:bg-gray-100"
                    aria-label="Remove photo"
                    data-testid="photo-preview-cancel"
                  >
                    <XIcon className="w-3.5 h-3.5 text-gray-700" />
                  </button>
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <Input
                    value={pendingPhoto.caption}
                    onChange={(e) => setPendingPhoto(p => ({ ...p, caption: e.target.value }))}
                    placeholder="Add a caption (optional)"
                    maxLength={500}
                    disabled={uploadingPhoto}
                    className="h-10 rounded-full"
                    data-testid="photo-caption-input"
                  />
                  <Button
                    onClick={handleSendPhoto}
                    disabled={uploadingPhoto}
                    className="h-10 rounded-full bg-[#009E60] hover:bg-[#007A4B]"
                    data-testid="send-photo-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {uploadingPhoto ? 'Sending…' : 'Send photo'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSend} className="flex gap-2 items-center">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePickPhoto}
                className="hidden"
                data-testid="photo-file-input"
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="h-12 w-12 rounded-full flex items-center justify-center hover:bg-gray-100 flex-shrink-0"
                aria-label="Send photo"
                data-testid="attach-photo-btn"
              >
                <ImagePlus className="w-6 h-6 text-[#009E60]" />
              </button>
              <VoiceRecorder
                onSend={handleSendVoice}
                onError={(m) => setError(m)}
                sending={sendingVoice}
              />
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 h-12 rounded-full border-gray-200 px-4"
                disabled={sending}
                data-testid="message-input"
              />
              <Button
                type="submit"
                className="h-12 w-12 rounded-full bg-[#009E60] hover:bg-[#007A4B] flex-shrink-0"
                disabled={!newMessage.trim() || sending}
                data-testid="send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
          )}

          {isPaid && canMessage && !user?.is_premium && (
            <p className="text-center text-xs text-gray-400 mt-2">
              {totalBalance} credit{totalBalance === 1 ? '' : 's'} remaining
              {user?.promo_credits > 0 && (
                <span className="text-[#009E60]"> · {user.promo_credits} bonus</span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Lightbox for enlarged photos */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            data-testid="photo-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
            <img src={lightbox} alt="Photo" className="max-w-full max-h-full object-contain rounded-lg" />
          </motion.div>
        )}
      </AnimatePresence>

      {showProfile && otherUser?.user_id && (
        <ProfileDetailModal
          userId={otherUser.user_id}
          onClose={() => setShowProfile(false)}
          onMessage={() => setShowProfile(false)}
          onBlocked={() => navigate('/messages')}
        />
      )}

      {paywall && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={() => setPaywall(null)} data-testid="chat-paywall">
          <div className="bg-white rounded-3xl w-full max-w-sm p-5 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 mx-auto rounded-full bg-yellow-100 flex items-center justify-center mb-3">
              <Crown className="w-7 h-7 text-yellow-600" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-1">{paywall.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{paywall.subtitle}</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => navigate(`/pricing?tab=credits&return=/messages/${matchId}`)}
                className="rounded-full bg-[#009E60] hover:bg-[#007A4B] text-white"
                data-testid="paywall-buy-credits"
              >
                Buy credits
              </Button>
              <Button
                onClick={() => navigate(`/pricing?tab=subscription&return=/messages/${matchId}`)}
                className="rounded-full bg-yellow-500 hover:bg-yellow-600 text-white"
                data-testid="paywall-subscribe"
              >
                Subscribe
              </Button>
            </div>
            <button onClick={() => setPaywall(null)} className="text-xs text-gray-500 mt-3" data-testid="paywall-close">Not now</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
